// Detect EU allergens (règlement INCO 1169/2011) from ingredient names
// Patterns are word-boundary regex on FR/EN names. Negative patterns (e.g. "lait
// de coco" should NOT match "Lait") are handled by exclusion regexes.

// All matching is case-insensitive and accent-insensitive
const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Each allergen maps to:
//  - match: array of regex patterns that should match
//  - exclude: array of patterns that explicitly DO NOT count (false positives)
const RULES = {
  Gluten: {
    match: [
      /\b(ble|wheat|froment|epeautre|spelt|orge|barley|seigle|rye|avoine|oats?|kamut|triticale|semoule|farine|flour|pates?|pasta|pain|bread|couscous|boulgour|bulgur|biscotte|brioche|chapelure|panko|breadcrumbs?)\b/,
    ],
    exclude: [
      /sans gluten/,
      /gluten-free/,
      // "pâte de curry/tomate/sésame/cacao/amande/piment/wasabi" etc. n'est pas du gluten
      /\bpate\s+(?:de|d.|aux?|au)\s+(?!ble|froment|epeautre|farine|pain)/,
    ],
  },
  "Œufs": {
    match: [/\b(oeufs?|egg|eggs|jaune d.?oeuf|blanc d.?oeuf|albumine|yolk|mayonnaise|meringue|omelette)\b/],
    exclude: [],
  },
  Lait: {
    // "Lait" but not "lait de coco/soja/amande/avoine"
    match: [
      /\blait\b/,
      /\b(milk|fromage|cheese|beurre(?!\s+de\s+(cacahuet|arachid|noix))|butter(?!\s+(peanut|nut))|creme|cream|yaourt|yogurt|yoghurt|mascarpone|ricotta|mozzarella|parmesan|gruyere|emmental|feta|brie|camembert|cheddar|caseine|casein|lactose|petit\s*lait|whey)\b/,
    ],
    exclude: [
      /\blait\s+de\s+(coco|soja|amande|avoine|riz|noisette|chanvre|cajou)\b/,
      /\bcoconut\s+milk\b/,
      /\bsoy?\s+milk\b/,
      /\balmond\s+milk\b/,
      /\boat\s+milk\b/,
      /\brice\s+milk\b/,
      /\bbeurre\s+de\s+cacao\b/,
      /\bcocoa\s+butter\b/,
    ],
  },
  Poisson: {
    match: [
      /\b(poisson|saumon|salmon|thon|tuna|cabillaud|cod|merlu|hake|sardine|anchois|anchov|maquereau|mackerel|truite|trout|hareng|herring|sole|bar|seabass|dorade|sea\s*bream|lotte|monkfish|raie|skate|colin|pollack|surimi|nuoc\s*mam|fish\s*sauce|sauce\s+poisson)\b/,
    ],
    exclude: [],
  },
  "Crustacés": {
    match: [
      /\b(crevettes?|shrimps?|prawns?|gambas|langoustines?|homard|lobster|crabe|crab|ecrevisse|crayfish|tourteau)\b/,
    ],
    exclude: [],
  },
  Arachides: {
    match: [/\b(arachides?|cacahu[èe]tes?|peanuts?)\b/],
    exclude: [],
  },
  "Fruits à coque": {
    match: [
      /\b(amandes?|almond|noisettes?|hazelnut|noix(?:\s+(?:de\s+)?(?:cajou|cashew|brésil|brazil|macadamia|pecan|p[ée]can))?|pistaches?|pistachios?|cajou|cashew|macadamia|noyer|walnut|p[ée]can|brazil\s*nut)\b/,
    ],
    exclude: [
      /\bnoix\s+de\s+coco\b/,
      /\bcoconut\b/,
      /\bnoix\s+de\s+muscade\b/,
      /\bnutmeg\b/,
    ],
  },
  Soja: {
    match: [/\b(soja|soy|soybean|tofu|tempeh|edamame|miso|tamari|sauce\s+soja|soy\s+sauce|shoyu)\b/],
    exclude: [],
  },
  "Céleri": {
    match: [/\b(c[ée]leri|celery|celeriac)\b/],
    exclude: [],
  },
  Moutarde: {
    match: [/\b(moutarde|mustard)\b/],
    exclude: [],
  },
  "Sésame": {
    match: [/\b(s[ée]same|tahini|tahin)\b/],
    exclude: [],
  },
  Sulfites: {
    match: [/\b(sulfites?|dioxyde\s+de\s+soufre|so2|vin\s+blanc|vin\s+rouge|red\s+wine|white\s+wine|wine|fruits?\s+secs?\s+sulfit)\b/],
    exclude: [],
  },
  Lupin: {
    match: [/\b(lupin|lupine|lupini)\b/],
    exclude: [],
  },
  Mollusques: {
    match: [
      /\b(moules?|mussels?|huitres?|oysters?|coquilles?\s+saint.?jacques?|scallops?|coques|cockles|seiche|cuttlefish|calmar|squid|encornet|poulpe|octopus|escargots?|snails?|bigorneaux|periwinkles?|palourdes?|clams?)\b/,
    ],
    exclude: [],
  },
};

/**
 * detectAllergens(ingredients)
 * @param {Array<{description?: string, name_fr?: string, brand?: string}>} ingredients
 * @returns {{ allergen: string, evidence: string[] }[]}  — only allergens with at least 1 match
 */
export const detectAllergens = (ingredients = []) => {
  const haystacks = ingredients
    .map((i) => ({
      label: i.name_fr || i.description || "",
      norm: normalize(`${i.name_fr || ""} ${i.description || ""} ${i.brand || ""}`),
    }))
    .filter((h) => h.norm.trim().length > 0);

  const findings = [];

  for (const [allergen, rules] of Object.entries(RULES)) {
    const evidence = [];
    for (const h of haystacks) {
      // Skip if any exclusion pattern matches this ingredient
      const excluded = rules.exclude.some((rx) => rx.test(h.norm));
      if (excluded) continue;
      const hit = rules.match.some((rx) => rx.test(h.norm));
      if (hit) evidence.push(h.label);
    }
    if (evidence.length > 0) {
      findings.push({ allergen, evidence });
    }
  }
  return findings;
};

/**
 * Returns the subset of detected allergens that are NOT already in the current
 * list — i.e. the ones we should suggest to the user.
 */
export const suggestNewAllergens = (ingredients, currentAllergens = []) => {
  const detected = detectAllergens(ingredients);
  const current = new Set(currentAllergens);
  return detected.filter((d) => !current.has(d.allergen));
};
